import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase, setSupabaseUserContext } from '../lib/supabase';

interface ClerkUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  permissions: string[];
}

interface SyncResult {
  success: boolean;
  user?: ClerkUserProfile;
  error?: string;
}

export function useClerkIntegration() {
  const { user, isLoaded } = useUser();
  const [isSynced, setIsSynced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncAttempted, setSyncAttempted] = useState(false);

  const syncUserWithDatabase = async (): Promise<SyncResult> => {
    if (!user) {
      return { success: false, error: 'No user found' };
    }

    try {
      setIsLoading(true);
      setError(null);

      const email = user.emailAddresses[0]?.emailAddress;
      
      if (!email) {
        throw new Error('No email address found');
      }


      // First, try to update existing profile by email with Clerk user ID
      // Check if profile exists by email and update with Clerk user ID
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, email, user_id')
        .eq('email', email)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing profile:', checkError);
        throw new Error(checkError.message);
      }

      if (existingProfile) {
        // Profile exists, update it with Clerk user ID
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            user_id: user.id,
            first_name: user.firstName || existingProfile.first_name,
            last_name: user.lastName || existingProfile.last_name,
            last_login: new Date().toISOString()
          })
          .eq('email', email);

        if (updateError) {
          console.error('Error updating profile:', updateError);
          throw new Error(updateError.message);
        }
        
        // Set up Supabase user context
        await setSupabaseUserContext(user.id);
        
        setIsSynced(true);
        return { success: true, user: existingProfile };
      } else {
        // Profile doesn't exist, try the RPC function to create it
        const { data, error: syncError } = await supabase.rpc('sync_clerk_user', {
          p_email: email,
          p_user_id: user.id, // Clerk user ID is already a string
          p_first_name: user.firstName || null,
          p_last_name: user.lastName || null
        });

        if (syncError) {
          console.error('Sync error:', syncError);
          throw new Error(syncError.message);
        }

        if (data && data.success) {
          // Set up Supabase user context
          await setSupabaseUserContext(user.id);
          
          setIsSynced(true);
          return { success: true, user: data.user };
        } else {
          console.error('Sync failed:', data);
          throw new Error(data?.error || 'Failed to sync user');
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync user';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const checkIsAdmin = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const email = user.emailAddresses[0]?.emailAddress;
      if (!email) return false;

      const { data, error } = await supabase.rpc('is_admin_by_email', {
        p_email: email
      });

      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }

      return data || false;
    } catch (err) {
      console.error('Error checking admin status:', err);
      return false;
    }
  };

  // Auto-sync when user loads
  useEffect(() => {
    if (isLoaded && user && !isSynced && !syncAttempted) {
      setSyncAttempted(true);
      // Add a small delay to ensure Clerk is fully loaded
      const timer = setTimeout(() => {
        syncUserWithDatabase();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isLoaded, user, isSynced, syncAttempted]);

  return {
    syncUserWithDatabase,
    checkIsAdmin,
    isSynced,
    isLoading,
    error
  };
}

// Hook for getting user profile by email (useful for admin functions)
export function useUserProfileByEmail(email: string) {
  const [profile, setProfile] = useState<ClerkUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!email) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase.rpc('get_user_profile_by_email', {
        p_email: email
      });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (data && data.length > 0) {
        setProfile(data[0]);
      } else {
        setProfile(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profile';
      setError(errorMessage);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [email]);

  return {
    profile,
    isLoading,
    error,
    refetch: fetchProfile
  };
}
